import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Grid,
  Divider,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { toast } from "react-toastify";
import { addDays, getDay, format, startOfDay, subYears } from "date-fns";
import { usePermissions } from "../../hooks/usePermissions";
import {
  EDIT_LECTURE_OVERRIDE,
  EDIT_LECTURE_OWN_OVERRIDE,
} from "../../utils/editLecturePermissions";
import { DATE_DISPLAY_FORMAT } from "../../utils/dateUtils";
import editLectureService from "../../services/editLectureService";
import api from "../../services/api";
import courseService from "../../services/courseService";

/**
 * Map dayOfWeek string/number from slot to JS Date getDay() value (0=Sun, 1=Mon, ...).
 * The backend stores dayOfWeek as a string like "Monday", "Tuesday", etc.
 */
const DAY_NAME_TO_NUMBER = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

function getDayNumber(dayOfWeek) {
  if (dayOfWeek == null) return -1;
  if (typeof dayOfWeek === "number") return dayOfWeek;
  const normalized = String(dayOfWeek).toLowerCase().trim();
  return DAY_NAME_TO_NUMBER[normalized] ?? -1;
}

/** Helper to avoid nested ternary in JSX */
function getDateHelperText(isDateValid, overrideDate, dayOfWeek) {
  if (!isDateValid && overrideDate) {
    return "Date must match the slot's weekday (past dates allowed, up to 7 days ahead)";
  }
  return `Only ${dayOfWeek || "matching"} dates allowed`;
}

/** Helper for teacher dropdown helper text */
function getTeacherHelperText(teachersError, hasOwnOverrideOnly, onRetry) {
  if (teachersError) {
    return (
      <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {teachersError}
        <Chip label="Retry" size="small" onClick={onRetry} sx={{ ml: 1, cursor: "pointer" }} />
      </Box>
    );
  }
  if (hasOwnOverrideOnly) {
    return "Teacher selection disabled (own schedule only)";
  }
  return null;
}

/**
 * EditLectureForm — Modal component for creating edit overrides on timetable slots.
 *
 * Props:
 * - open: boolean — controls dialog visibility
 * - onClose: () => void — called when dialog should close
 * - slot: object — { id, teacher, subject, startTime, endTime, dayOfWeek, classId, className }
 *     teacher: { id, name }
 *     subject: { id, name }
 * - date: Date | string — the date the parent surfaced (used to seed overrideDate;
 *     may be a PAST date — past overrides are allowed and gated server-side by
 *     marked attendance)
 * - onSuccess: () => void — called after successful override creation (e.g., refresh parent)
 */
const EditLectureForm = ({ open, onClose, slot, date, onSuccess }) => {
  const { hasPermission } = usePermissions();
  const hasOverridePermission = hasPermission(EDIT_LECTURE_OVERRIDE);
  const hasOwnOverrideOnly =
    hasPermission(EDIT_LECTURE_OWN_OVERRIDE) && !hasOverridePermission;

  // --- State ---
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [overrideDate, setOverrideDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Teacher dropdown state
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [teachersError, setTeachersError] = useState(null);

  // Subject dropdown state
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectsError, setSubjectsError] = useState(null);

  // --- Derived values ---
  const slotDayNumber = useMemo(() => getDayNumber(slot?.dayOfWeek), [slot?.dayOfWeek]);

  const today = useMemo(() => startOfDay(new Date()), []);
  const maxDate = useMemo(() => addDays(today, 7), [today]);
  // Past dates are allowed (gated server-side by marked attendance); use a far-past
  // floor so the picker stays usable for historical edits.
  const minDate = useMemo(() => subYears(today, 1), [today]);

  // Seed the override date. Prefer the parent-provided `date` (the day the staff is
  // viewing) when its weekday matches the slot — this may be a PAST date. Otherwise
  // fall back to today (if it matches) or the next matching weekday within 7 days.
  useEffect(() => {
    if (!open || slotDayNumber < 0) return;

    if (date) {
      const parsed = date instanceof Date ? date : new Date(date);
      if (!Number.isNaN(parsed?.getTime?.())) {
        const d0 = startOfDay(parsed);
        if (d0 >= minDate && d0 <= maxDate && getDay(d0) === slotDayNumber) {
          setOverrideDate(d0);
          return;
        }
      }
    }

    const todayDay = getDay(today);
    if (todayDay === slotDayNumber) {
      setOverrideDate(today);
    } else {
      // Find the next occurrence of this weekday within 7 days
      for (let i = 1; i <= 7; i++) {
        const candidate = addDays(today, i);
        if (getDay(candidate) === slotDayNumber) {
          setOverrideDate(candidate);
          break;
        }
      }
    }
  }, [open, slotDayNumber, today, minDate, maxDate, date]);

  // Pre-fill teacher and subject from slot when dialog opens
  useEffect(() => {
    if (!open || !slot) return;
    setSelectedTeacher(
      slot.teacher ? { id: slot.teacher.id, name: slot.teacher.name } : null
    );
    setSelectedSubject(
      slot.subject ? { id: slot.subject.id, name: slot.subject.name } : null
    );
  }, [open, slot]);

  // Scope the teacher/subject pickers to this lecture's department. The slot
  // carries classId (and often departmentId); the backend resolves the
  // department from classId when departmentId is absent. Omitting both keeps
  // the original institute-wide behavior.
  const scopeParams = useMemo(() => {
    const params = {};
    if (slot?.departmentId) params.departmentId = slot.departmentId;
    if (slot?.classId) params.classId = slot.classId;
    return params;
  }, [slot?.departmentId, slot?.classId]);

  // --- Fetch teacher list ---
  const fetchTeachers = useCallback(async () => {
    setTeachersLoading(true);
    setTeachersError(null);
    try {
      const response = await api.get("/teacher/get-teachers-names", {
        params: scopeParams,
      });
      const data = response?.data?.data || response?.data || [];
      const teacherList = Array.isArray(data)
        ? data.map((t) => ({
            id: t.id || t.teacherId,
            name: t.name || t.fullName || `${t.firstName || ""} ${t.lastName || ""}`.trim(),
          }))
        : [];
      setTeachers(teacherList);
    } catch (err) {
      setTeachersError(err?.response?.data?.message || "Failed to load teachers");
    } finally {
      setTeachersLoading(false);
    }
  }, [scopeParams]);

  // --- Fetch subject list ---
  const fetchSubjects = useCallback(async () => {
    setSubjectsLoading(true);
    setSubjectsError(null);
    try {
      const response = await courseService.getAllSubjects({
        limit: 500,
        ...scopeParams,
      });
      const data = response?.data?.data?.subjects || response?.data?.data || response?.data || [];
      const subjectList = Array.isArray(data)
        ? data.map((s) => ({
            id: s.id,
            name: s.name || s.subjectName || "",
          }))
        : [];
      setSubjects(subjectList);
    } catch (err) {
      setSubjectsError(err?.response?.data?.message || "Failed to load subjects");
    } finally {
      setSubjectsLoading(false);
    }
  }, [scopeParams]);

  // Fetch dropdown data when dialog opens
  useEffect(() => {
    if (open) {
      fetchTeachers();
      fetchSubjects();
    }
  }, [open, fetchTeachers, fetchSubjects]);

  // Reset form on close
  useEffect(() => {
    if (!open) {
      setSelectedTeacher(null);
      setSelectedSubject(null);
      setOverrideDate(null);
      setSubmitting(false);
    }
  }, [open]);

  // --- Validation ---
  const hasChanged = useMemo(() => {
    if (!slot) return false;
    const teacherChanged =
      selectedTeacher?.id != null && selectedTeacher.id !== slot.teacher?.id;
    const subjectChanged =
      selectedSubject?.id != null && selectedSubject.id !== slot.subject?.id;
    return teacherChanged || subjectChanged;
  }, [slot, selectedTeacher, selectedSubject]);

  const isDateValid = useMemo(() => {
    if (!overrideDate) return false;
    const d = startOfDay(overrideDate);
    // Past dates allowed; only enforce the far-past floor and the +7 forward cap.
    if (d < minDate || d > maxDate) return false;
    // Must match slot's weekday
    if (getDay(d) !== slotDayNumber) return false;
    return true;
  }, [overrideDate, minDate, maxDate, slotDayNumber]);

  const canSubmit =
    hasChanged && isDateValid && !submitting && !teachersLoading && !subjectsLoading;

  // --- Date restriction ---
  const shouldDisableDate = useCallback(
    (date) => {
      if (!date) return true;
      const d = date instanceof Date ? date : new Date(date);
      // Only allow the same weekday as the slot
      return getDay(d) !== slotDayNumber;
    },
    [slotDayNumber]
  );

  // --- Submit ---
  const handleSubmit = async () => {
    if (!canSubmit || !slot) return;
    setSubmitting(true);
    try {
      const body = {
        timeTableSlotId: slot.id,
        overrideDate: format(overrideDate, "yyyy-MM-dd"),
      };
      // Only send fields that actually changed
      if (selectedTeacher?.id != null && selectedTeacher.id !== slot.teacher?.id) {
        body.overrideTeacherId = selectedTeacher.id;
      }
      if (selectedSubject?.id != null && selectedSubject.id !== slot.subject?.id) {
        body.overrideSubjectId = selectedSubject.id;
      }
      await editLectureService.createEditOverride(body);
      toast.success("Edit override applied successfully");
      onSuccess?.();
      onClose?.();
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to create edit override";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!slot) return null;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>Edit Lecture Override</DialogTitle>
      <DialogContent dividers>
        {/* Read-only slot context */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Slot Details
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Class:</strong> {slot.className || "—"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Day:</strong> {slot.dayOfWeek || "—"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Teacher:</strong> {slot.teacher?.name || "—"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Subject:</strong> {slot.subject?.name || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Time:</strong> {slot.startTime || "—"} – {slot.endTime || "—"}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Override fields */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Override Values
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {/* Teacher Dropdown */}
          <Autocomplete
            options={teachers}
            getOptionLabel={(option) => option?.name || ""}
            value={selectedTeacher}
            onChange={(_, value) => setSelectedTeacher(value)}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            disabled={hasOwnOverrideOnly || teachersLoading}
            loading={teachersLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Override Teacher"
                placeholder="Search teachers..."
                error={Boolean(teachersError)}
                helperText={getTeacherHelperText(teachersError, hasOwnOverrideOnly, fetchTeachers)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {teachersLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Subject Dropdown */}
          <Autocomplete
            options={subjects}
            getOptionLabel={(option) => option?.name || ""}
            value={selectedSubject}
            onChange={(_, value) => setSelectedSubject(value)}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            disabled={subjectsLoading}
            loading={subjectsLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Override Subject"
                placeholder="Search subjects..."
                error={Boolean(subjectsError)}
                helperText={
                  subjectsError ? (
                    <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {subjectsError}
                      <Chip
                        label="Retry"
                        size="small"
                        onClick={fetchSubjects}
                        sx={{ ml: 1, cursor: "pointer" }}
                      />
                    </Box>
                  ) : undefined
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {subjectsLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Override Date Picker */}
          <DatePicker
            label="Override Date"
            format={DATE_DISPLAY_FORMAT}
            value={overrideDate}
            onChange={(value) => setOverrideDate(value)}
            minDate={minDate}
            maxDate={maxDate}
            shouldDisableDate={shouldDisableDate}
            slotProps={{
              textField: {
                fullWidth: true,
                helperText: getDateHelperText(isDateValid, overrideDate, slot?.dayOfWeek),
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {submitting ? "Applying..." : "Apply Override"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditLectureForm;
